import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@atlas/shared/client/ui/wc/components/rating';
import '@atlas/shared/client/ui/wc/components/input';
import '@atlas/shared/client/ui/wc/components/button';
import '@atlas/shared/client/ui/wc/icons/IconCheck';
import { ChatbotService, type RateConversationRequestModel } from '@atlas/shared/data-access/chatbot';
import { consume } from '@lit/context';
import { chatbotMessageContext, type ChatbotMessageContext } from '../chatbot-message/ChatbotMessageContext';
import { SuperElement } from '@atlas/shared/client/ui/wc/internals/super/SuperElement';
import { BroadcastController } from '../../controllers/BroadcastController';
import {
    LR_CHATBOT_BROADCAST_CHANNEL_ID,
    LR_CHATBOT_LOCALSTORAGE_CONVERSATION_ID,
    type ChatbotBroadcastMessage
} from '../chatbot/Chatbot';
import { t } from 'i18next';
import { classMap } from 'lit/directives/class-map.js';
import { chatbotConversationRating } from './ChatbotConversationRating.styles';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

@customElement('lr-chatbot-conversation-rating')
export class ChatbotConversationRating extends SuperElement {
    @consume({ context: chatbotMessageContext })
    messageContext?: ChatbotMessageContext;
    @state()
    score: number | null = null;
    @property({ type: String })
    label = '';
    @state()
    rating = 0;
    @state()
    feedback = '';

    private _broadcastController = new BroadcastController<ChatbotBroadcastMessage>(
        this,
        LR_CHATBOT_BROADCAST_CHANNEL_ID
    );
    private getConversationId() {
        return localStorage.getItem(LR_CHATBOT_LOCALSTORAGE_CONVERSATION_ID);
    }
    static styles = [chatbotConversationRating];
    async handleSubmit(event: SubmitEvent) {
        event.preventDefault();

        if (!this.messageContext?.apiUrl || !this.messageContext?.conversationId) {
            throw Error('Missing information to call API on message rating');
        }

        try {
            const form = event.target as HTMLFormElement;
            if (form) {
                const formData = new FormData(form);
                const service = new ChatbotService({
                    baseURL: this.messageContext.apiUrl,
                    withCredentials: true,
                    method: 'POST'
                });
                const score = Number(formData.get('rating'));
                this.rating = score;
                if (this.rating > 0) {
                    if (this.feedback) {
                        return;
                    }
                    this.feedback = (formData.get('feedback') as string) || '';
                    this.score = score;

                    const postData: RateConversationRequestModel = {
                        score,
                        feedback: this.feedback
                    };

                    await service.chatbotConversationsRatePost({ id: this.messageContext.conversationId }, postData);
                }

                const currentConversationId = this.getConversationId();
                if (!currentConversationId) {
                    if (!this.feedback && this.score) {
                        this._broadcastController.send({
                            action: 'addMessage',
                            payload: {
                                message: {
                                    html: t('chatbot:otherQuestiononConversation')
                                }
                            }
                        });
                    }
                }
                if (this.feedback) {
                    this.score = null;
                }
            }
        } catch {}
    }

    renderFeedbackElement() {
        const showFeedbackForm = this.score && this.score <= 3 ? true : false;
        const feedbackSent = this.feedback && !this.score && !showFeedbackForm;
        const classes = classMap({
            feedback: true,
            'feedback-content': showFeedbackForm,
            'feedback-confirm': feedbackSent
        });
        return html`
            <div class="${classes}">
                ${showFeedbackForm
                    ? html`<lr-input
                              label="${t('chatbot:feedback.inputLabel')}"
                              placeholder="${t('chatbot:feedback.inputPlaceholder')}"
                              name="feedback"
                              .value="${this.feedback}"
                          ></lr-input>
                          <lr-button fluid size="small"> ${t('chatbot:feedback.sendButtonLabel')} </lr-button>`
                    : feedbackSent
                    ? html`${unsafeHTML(t('chatbot:feedback.messageConfirm'))}`
                    : nothing}
            </div>
        `;
    }

    renderElement() {
        return html`
            <form @submit="${this.handleSubmit}">
                <p>${this.label}</p>
                <lr-rating
                    name="rating"
                    size="small"
                    value="${this.rating}"
                    ?submitOnChange="${!this.rating}"
                ></lr-rating>
                ${this.renderFeedbackElement()}
            </form>
        `;
    }
}
